import { useState } from 'react';

import { CloseCircleFilled } from '@ant-design/icons';
import { Button, Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { validateEmail } from '@revenuemarket/utils';
import { Form, Input, Typography } from 'antd';
import dayjs from 'dayjs';

import {
  useGetInvoiceIssuanceInfo,
  useResendEmail,
  useRetryEmailSend,
} from '~hooks/queries/tax-invoice/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { css } from '~libs/emotion';
import { EmailResendItem, EmailResendStatus } from '~models/tax-invoice';

import { TAX_INVOICE_STATUS } from './type';

const commonTagStyle = `
  display: flex;
  height: 20px;
  padding: 8px 4px;
  justify-content: center;
  align-items: center;
  gap: 2px;
  border-radius: 5px;
`;

const StatusTag = (status: EmailResendStatus, id: number, onRetry: (resendId: number) => void) => {
  switch (status) {
    case 'COMPLETED':
      return (
        <div
          className={css`
            ${commonTagStyle}
            background-color: ${Colors.blueOpacity12};
          `}
        >
          <Typography.Text style={{ ...typographyStyle['13_500'], color: Colors.blue900 }}>
            성공
          </Typography.Text>
        </div>
      );
    case 'FAILED':
    case 'FATAL':
      return (
        <Flex align="center" gap={8}>
          <div
            className={css`
              ${commonTagStyle}
              background-color: ${Colors.redOpacity12};
            `}
          >
            <Typography.Text style={{ ...typographyStyle['13_500'], color: Colors.red700 }}>
              실패
            </Typography.Text>
          </div>
          {status === 'FAILED' ? (
            <Button
              buttonSize="small"
              border
              style={{
                padding: '0px 6px',
                display: 'felx',
                alignItems: 'center',
              }}
              onClick={() => onRetry(id)}
            >
              <Typography.Text
                style={{
                  ...typographyStyle['13_500'],
                  color: Colors.gray700,
                }}
              >
                다시 보내기
              </Typography.Text>
            </Button>
          ) : (
            <div />
          )}
        </Flex>
      );
    case 'SENDING':
      return (
        <div
          className={css`
            ${commonTagStyle}
            background-color: ${Colors.grayOpacity07};
          `}
        >
          <Typography.Text style={{ ...typographyStyle['13_500'], color: Colors.gray700 }}>
            확인중...
          </Typography.Text>
        </div>
      );
  }
};

const TaxInvoiceMailResendListItem: React.FC<
  EmailResendItem & {
    invoiceIssuanceId: number;
  }
> = ({ email, createdAt, updatedAt, status, id, invoiceIssuanceId }) => {
  const { mutateAsync: retryEmailSend } = useRetryEmailSend();
  const { companyId } = useCompanyId();
  const onRetry = async (resendId: number) => {
    const response = await retryEmailSend({ companyId, invoiceIssuanceId, resendId });

    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('메일을 다시 보냈어요');
    } else {
      Toast.error('메일을 다시 보내지 못했어요');
    }
  };
  return (
    <Flex justify="space-between" align="center">
      <Flex
        direction="column"
        gap={2}
        style={{
          maxWidth: 300,
        }}
      >
        <Typography.Text style={{ ...typographyStyle['14_500'], color: Colors.gray800 }}>
          {email}
        </Typography.Text>
      </Flex>
      <Flex gap={4} align="center">
        <Typography.Text style={{ ...typographyStyle['13_400'], color: Colors.gray800 }} ellipsis>
          {dayjs(updatedAt || createdAt).format('YYYY.MM.DD HH:mm')}
        </Typography.Text>
        {id ? StatusTag(status, id, onRetry) : <div />}
      </Flex>
    </Flex>
  );
};

const MAX_LENGTH = 40;
interface Props {
  status?: TAX_INVOICE_STATUS;
  id?: number;
}

const TaxInvoiceMailResendList: React.FC<Props> = ({ status, id }) => {
  const { companyId } = useCompanyId();
  const [referenceEmail, setReferenceEmail] = useState('');
  const { mutateAsync: resend } = useResendEmail();
  const [emailReferenceStatus, setEmailReferenceStatus] = useState<
    'error' | 'error-max-length' | undefined
  >();

  if (!id || status === 'FAILED' || status === 'IN_PROGRESS') return null;

  const { data: invoiceData, refetch } = useGetInvoiceIssuanceInfo({
    companyId,
    invoiceIssuanceId: id,
  });

  const emailResends = invoiceData?.Data?.data?.emailResends || [];

  const onResend = async () => {
    const response = await resend({
      companyId,
      invoiceIssuanceId: id,
      resendEmails: [referenceEmail],
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      setReferenceEmail('');
      refetch();
      Toast.success('메일을 보냈어요');
    } else {
      Toast.error('메일을 다시 보내지 못했어요');
    }
  };

  const emailError =
    emailReferenceStatus === 'error' || emailReferenceStatus === 'error-max-length';

  return (
    <Flex direction="column" gap={26}>
      {emailResends?.length ? (
        <Flex direction="column">
          <Typography.Text
            style={{
              ...typographyStyle['13_400'],
              color: Colors.gray500,
              marginBottom: 12,
            }}
          >
            세금계산서 이메일 발송 이력
          </Typography.Text>
          <Flex direction="column" gap={12}>
            {emailResends.map((item, index) => (
              <TaxInvoiceMailResendListItem key={index} invoiceIssuanceId={id} {...item} />
            ))}
          </Flex>
        </Flex>
      ) : null}
      <Flex direction="column">
        <Typography.Text
          style={{
            ...typographyStyle['13_400'],
            color: Colors.gray500,
            marginBottom: 12,
          }}
        >
          이메일 추가 발송
        </Typography.Text>
        <Form.Item
          style={{
            width: '100%',
          }}
          help={
            emailError
              ? emailReferenceStatus === 'error-max-length'
                ? `
          ${MAX_LENGTH}자 이내로 입력해주세요.
          `
                : '이메일 형식이 올바르지 않습니다.'
              : undefined
          }
          validateStatus={emailError ? 'error' : undefined}
        >
          <Flex align="center" gap={8}>
            <Input
              placeholder="이메일을 입력하세요. 세금계산서에는 기재되지 않아요."
              suffix={
                <CloseCircleFilled
                  style={{
                    color: '#F74B46',
                    visibility: emailError ? 'visible' : 'hidden',
                  }}
                />
              }
              status={emailError ? 'error' : undefined}
              value={referenceEmail}
              onChange={(e) => {
                if (e.target.value?.length > MAX_LENGTH) {
                  setEmailReferenceStatus('error-max-length');
                } else if (!validateEmail(e.target.value)) {
                  setEmailReferenceStatus('error');
                } else {
                  setEmailReferenceStatus(undefined);
                }

                setReferenceEmail(e.target.value);
              }}
            />
            <Button
              border
              type="button"
              disable={emailError || !referenceEmail}
              onClick={onResend}
              style={{
                width: 50,
              }}
            >
              보내기
            </Button>
          </Flex>
        </Form.Item>
      </Flex>
    </Flex>
  );
};

export default TaxInvoiceMailResendList;
